import { useEffect, useState, useCallback } from "react";
import { Activity, AlertTriangle, CheckCircle2, Calendar, TrendingUp, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import HealthScoreBadge from "@/components/HealthScoreBadge";

const STATUS_TONE = {
  excellent:   "bg-[#E6F6ED] border-[#bce5cc] text-[#166534]",
  good:        "bg-[#E6F6ED] border-[#bce5cc] text-[#166534]",
  at_risk:     "bg-[#fff5e6] border-[#fcd9a3] text-[#B45309]",
  critical:    "bg-[#FDE8E6] border-[#F5B6B0] text-[#B91C1C]",
  not_started: "bg-[#FDFBF7] border-[#E0E4E2] text-[#5C6B68]",
  no_plan:     "bg-[#FDFBF7] border-[#E0E4E2] text-[#5C6B68]",
};

function Bar({ pct, tone }) {
  return (
    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-[#E0E4E2]">
      <div
        className={tone}
        style={{ width: `${Math.max(2, Math.min(100, pct || 0))}%`, height: "100%", transition: "width 0.6s ease-out" }}
      />
    </div>
  );
}

/**
 * Treatment-plan compliance + composite health-score card.
 * Renders inline for attorneys, admins, and (gamified copy) for claimants.
 *
 * Props:
 *   caseId: string (required)
 *   variant: 'attorney' | 'admin' | 'claimant'
 *   refreshKey: any (change to refetch — used after adding a treatment)
 */
export default function ComplianceCard({ caseId, variant = "attorney", refreshKey }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get(`/cases/${caseId}/compliance`);
      setData(resp);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (loading) {
    return (
      <div className="card-soft flex items-center gap-3 p-6" data-testid="compliance-loading">
        <Loader2 className="h-4 w-4 animate-spin text-[#11322A]" />
        <span className="text-sm text-[#5C6B68]">{t("compliance.loading")}</span>
      </div>
    );
  }
  if (!data) return null;

  const { compliance: c, health } = data;
  const status = c.status || "no_plan";
  const tone = STATUS_TONE[status];
  const barTone =
    status === "excellent" || status === "good"
      ? "bg-[#2D6A4F]"
      : status === "at_risk"
        ? "bg-[#E85D04]"
        : status === "critical"
          ? "bg-[#B91C1C]"
          : "bg-[#11322A]";

  return (
    <div className="card-soft p-6" data-testid="compliance-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="label-cap text-[10px]">
            {variant === "claimant" ? t("compliance.claimant.title") : t("compliance.title")}
          </div>
          <h3 className="mt-1 font-display text-xl font-black text-[#11322A]">
            {t(`compliance.status.${status}`)}
          </h3>
        </div>
        <HealthScoreBadge health={health} />
      </div>

      {!c.has_plan && (
        <p className="mt-4 text-sm text-[#5C6B68]" data-testid="compliance-no-plan">
          {variant === "claimant" ? t("compliance.claimant.no_plan") : t("compliance.no_plan")}
        </p>
      )}

      {c.has_plan && (
        <>
          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            <div>
              <div className="label-cap text-[9px]">{t("compliance.score")}</div>
              <div className="mt-1 font-display text-4xl font-black text-[#11322A]">
                {c.score}<span className="text-lg text-[#5C6B68]">%</span>
              </div>
              <Bar pct={c.score} tone={barTone} />
            </div>
            <div>
              <div className="label-cap text-[9px]">{t("compliance.visits")}</div>
              <div className="mt-1 font-display text-2xl font-black text-[#11322A]">
                {c.actual_visits}<span className="mx-1 text-base text-[#5C6B68]">/</span>{c.expected_visits}
              </div>
              <div className="mt-1 text-[11px] text-[#5C6B68]">{t("compliance.actual_vs_expected")}</div>
            </div>
            <div>
              <div className="label-cap text-[9px]">{t("compliance.plan")}</div>
              <div className="mt-1 font-display text-lg font-black text-[#11322A]">
                {c.visits_per_week}× / {t("compliance.week_short")}
              </div>
              <div className="mt-1 text-[11px] text-[#5C6B68]">
                {t("compliance.weeks_progress", { done: c.weeks_elapsed, total: c.plan_weeks })}
              </div>
            </div>
          </div>

          <div className={`mt-5 inline-flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${tone}`}>
            {status === "critical" || status === "at_risk" ? (
              <AlertTriangle className="h-3.5 w-3.5" />
            ) : (
              <Activity className="h-3.5 w-3.5" />
            )}
            <span className="flex-1">
              {t(variant === "claimant" ? `compliance.tip.claimant.${status}` : `compliance.tip.${status}`)}
            </span>
          </div>

          {c.has_gap_alert && (
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#F5B6B0] bg-[#FDE8E6] p-3 text-sm text-[#B91C1C]" data-testid="compliance-gap-alert">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-display font-bold">{t("compliance.gap_alert_title")}</div>
                <div className="text-xs">{t("compliance.gap_alert_body", { days: c.gap_days })}</div>
              </div>
            </div>
          )}
        </>
      )}

      {variant !== "claimant" && (
        <div className="mt-5 grid gap-2 border-t border-[#E0E4E2] pt-4 text-[11px] text-[#5C6B68]">
          <div className="font-bold uppercase tracking-[0.12em]">{t("compliance.health_breakdown")}</div>
          <div className="grid grid-cols-3 gap-2">
            <span><span className="font-bold text-[#11322A]">{health.breakdown.compliance}</span>/40 {t("compliance.label.compliance")}</span>
            <span><span className="font-bold text-[#11322A]">{health.breakdown.velocity}</span>/30 {t("compliance.label.velocity")}</span>
            <span><span className="font-bold text-[#11322A]">{health.breakdown.recency}</span>/30 {t("compliance.label.recency")}</span>
          </div>
        </div>
      )}
    </div>
  );
}
